import React, { FC, memo, useMemo } from 'react';
import { useLocation } from 'react-router';

import { useAccountModal } from '@rainbow-me/rainbowkit';

import { useGetRoleMember } from '@services/contract/fusion/nftFusion.contract';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { isAddressesEqual } from '@utils/tokens.utils';

import logo from '@assets/logos/logo.svg?url';

import Button from '@components/common/Button';
import Container from '@components/common/Container';
import Link from '@components/common/Link';

import { DEFAULT_CAIN_ID } from '@constants/common/chain.constants';
import { ADMINS_LIST_ADDRESS } from '@constants/common/common.constants';
import { HEADER_ROUTES } from '@constants/common/menu.constants';
import {
  PATH_ADMIN,
  PATH_INDEX,
  PATH_NFT_TRANSFER,
} from '@constants/common/routes.constants';
import { Variant } from '@constants/common/ui.constants';

import useStyles from '../styles.module';

const HeaderContainer: FC = () => {
  const { classes } = useStyles();
  const { account } = useAuth();
  const { pathname } = useLocation();
  const { openAccountModal } = useAccountModal();

  const isAdmin = useMemo(
    () => ADMINS_LIST_ADDRESS.find((item) => isAddressesEqual(account, item)),
    [account],
  );

  const { data: roleMemberList } = useGetRoleMember(DEFAULT_CAIN_ID);

  const isTransferWhitelisted = useMemo(
    () =>
      !!roleMemberList &&
      !!account &&
      roleMemberList.some((item) => isAddressesEqual(account, item)),
    [roleMemberList, account],
  );

  const renderNavButtons = useMemo(
    () =>
      HEADER_ROUTES.map(({ title, href, isNewTab }) => {
        const isActive = href === pathname;

        if (href === PATH_ADMIN && !isAdmin) return null;
        if (href === PATH_NFT_TRANSFER && !isTransferWhitelisted) return null;

        return (
          <Link
            key={title}
            variant="subtitle1"
            pathname={href}
            isNewTab={isNewTab}
            color={(theme) => theme.palette.custom.text.light}
            activeColor={(theme) => theme.palette.custom.text.active}
            isActive={isActive}
          >
            {title}
          </Link>
        );
      }),
    [pathname, isAdmin, isTransferWhitelisted],
  );

  return (
    <div className={classes.header}>
      <Container className={classes.row}>
        <Link pathname={PATH_INDEX}>
          <img src={logo} alt="" />
        </Link>
        <div className={classes.inner}>
          <nav className={classes.nav}>{renderNavButtons}</nav>
        </div>
        <div className={classes.buttonWrap}>
          {account ? (
            <Button onClick={openAccountModal} variant={Variant.Secondary}>
              My wallet
            </Button>
          ) : (
            <Button checkAuth variant={Variant.Secondary}>
              Connect Wallet
            </Button>
          )}
        </div>
      </Container>
    </div>
  );
};

export default memo(HeaderContainer);
